import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { HAFHOwnerSeed } from "./data/HAFHSeed";

import HAFHAttendanceDisplay from "./components/HAFHAttendanceDisplay";
import HAFHMessagesDisplay from "./components/HAFHMessagesDisplay";
import HAFHDosDont from "./components/HAFHDosDont";
import HAFHContatList from "./components/HAFHContactList";
import HAFHActivityList from "./components/HAFHActivityList";
import HAFHRoutineList from "./components/HAFHRoutineList";
import HAFHMessMenuList from "./components/HAFHMessMenuList";
import HAFHTransportList from "./components/HAFHTransportList";
import HAFHPhotos from "./components/HAFHPhotos";
import HAFHStudentRankings from "./components/HAFHStudentRankings";
import HAFHOnboarding from "./components/HAFHOnboarding";

import HAFHAI from "./HAFHAI";

const demoHostelCode = "THINK5WHYS_HOSTEL_001";

const TAB_ICONS = {
  signal: "🧠",
  attendance: "📍",
  messages: "💬",
  mess: "🍽️",
  transit: "🚌",
  activities: "🏃",
  contacts: "📞",
  hRoutine: "🔁",
  dosdont: "📘",
  photos: "📸",
  rankings: "🏆",
  applause: "👏",
  hafhprofile: "📝",
};

const PARENT_TABS = [
  "signal",
  "attendance",
  "messages",
  "mess",
  "transit",
  "activities",
  "contacts",
  "hRoutine",
  "dosdont",
  "photos",
  "rankings",
  "applause",
  "hafhprofile",
];

const HAFHParent = ({
  t,
  selectedLanguage,
  currentHostel,
  role,
  currentTheme,
}) => {
  const authState = useSelector((state) => state.authReducer || {});
  const navigate = useNavigate();

  const userRole = role;

  const hostelCode = demoHostelCode;

  const data =
    HAFHOwnerSeed[selectedLanguage] ||
    HAFHOwnerSeed["en"];

  const [activeTab, setActiveTab] = useState("signal");

  const student =
    data?.hostels?.[0]?.students?.[0];

  // =====================================================
  // COMMON THEME HELPERS
  // =====================================================

  const cardClass = `
    ${currentTheme.card}
    ${currentTheme.text}
    border
    ${currentTheme.border}
    rounded-2xl
    p-5
    shadow-lg
  `;

  const titleClass = `
    ${currentTheme.accent}
    text-lg
    font-semibold
  `;

  const paragraphClass = `
    ${currentTheme.subText}
    text-xs
    lg:text-sm
    leading-relaxed
  `;

  const buttonClass = `
    mt-8
    text-xs
    lg:text-sm
    px-4
    py-2
    rounded-xl
    transition-all
    ${currentTheme.button}
    ${currentTheme.buttonHover}
  `;

  // =====================================================
  // PARENT AI INSIGHTS
  // =====================================================

  const generateInsights = () => {
    if (!student) return [];

    const insights = [];

    const lateEntry = student.attendance?.find(
      (a) =>
        a.type === "Gate" &&
        a.in > "09:30"
    );

    if (lateEntry) {
      insights.push("You entered late today");
    }

    const messMiss =
      student.attendance?.filter(
        (a) => a.type === "Mess"
      );

    if (!messMiss?.length) {
      insights.push("You skipped meals");
    }

    return insights;
  };

  const insights = generateInsights();

  return (
    <div className={`${currentTheme.bg} ${currentTheme.text} space-y-5`}>

      {/* ===================================================== */}
      {/* TABS */}
      {/* ===================================================== */}

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {PARENT_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              flex items-center justify-center gap-2
              text-xs lg:text-sm
              min-w-[120px]
              px-2 py-1
              rounded-md
              border
              transition-all duration-200
              hover:text-white
              ${
                activeTab === tab
                  ? currentTheme.tabActive
                  : `${currentTheme.tabInactive} ${currentTheme.tabHover}`
              }
            `}
          >
            <span className="text-sm">{TAB_ICONS[tab]}</span>
            <span>{t(tab)}</span>
          </button>
        ))}
      </div>

      {/* ===================================================== */}
      {/* AI SIGNAL */}
      {/* ===================================================== */}

      {activeTab === "signal" && (
        <HAFHAI
          t={t}
          selectedLanguage={selectedLanguage}
          role={userRole}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* ATTENDANCE */}
      {/* ===================================================== */}

      {activeTab === "attendance" && student && (
        <div className="space-y-4">
          <HAFHAttendanceDisplay
            student={student}
            t={t}
            role={userRole}
            currentTheme={currentTheme}
          />
        </div>
      )}

      {/* ===================================================== */}
      {/* MESSAGES */}
      {/* ===================================================== */}

      {activeTab === "messages" && (
        <HAFHMessagesDisplay
          t={t}
          role={userRole}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* MESS */}
      {/* ===================================================== */}

      {activeTab === "mess" && (
        <HAFHMessMenuList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* TRANSIT */}
      {/* ===================================================== */}

      {activeTab === "transit" && (
        <HAFHTransportList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* CONTACTS */}
      {/* ===================================================== */}

      {activeTab === "contacts" && (
        <HAFHContatList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* ACTIVITIES */}
      {/* ===================================================== */}

      {activeTab === "activities" && (
        <HAFHActivityList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* ROUTINE */}
      {/* ===================================================== */}

      {activeTab === "hRoutine" && (
        <HAFHRoutineList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* DOS DONT */}
      {/* ===================================================== */}

      {activeTab === "dosdont" && (
        <HAFHDosDont
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* PHOTOS */}
      {/* ===================================================== */}

      {activeTab === "photos" && (
        <HAFHPhotos
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* RANKINGS */}
      {/* ===================================================== */}

      {activeTab === "rankings" && (
        <HAFHStudentRankings
          t={t}
          hostelCode={hostelCode}
          role={userRole}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* PERSONAL SPACE */}
      {/* ===================================================== */}

      {activeTab === "pSpace" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("pSpace")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              A daily diary and task system is a structured
              way for students to manage responsibilities,
              habits, and personal growth in hostel life.
            </p>

            <p className={paragraphClass}>
              Completing daily tasks helps students build
              consistency in academics, hygiene,
              attendance, and personal routines.
            </p>

            <p className={paragraphClass}>
              Writing self-notes improves self-awareness,
              accountability, and discipline.
            </p>

          </div>

          <button
            onClick={() => navigate("/hafhtodo")}
            className={buttonClass}
          >
            Go for Personal Space Dashboard
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* MY DIARY */}
      {/* ===================================================== */}

      {activeTab === "myDiary" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("myDiary")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              A hostel diary is an intelligent reflection
              of a student’s daily life built from
              attendance, routines, and habits.
            </p>

            <p className={paragraphClass}>
              It creates a structured timeline of
              discipline, growth, and accountability.
            </p>

            <p className={paragraphClass}>
              Over time, it becomes a personal growth
              history showing lifestyle evolution.
            </p>

          </div>

          <button
            onClick={() => navigate("/hafhdiary")}
            className={buttonClass}
          >
            My Diary
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* APPLAUSE */}
      {/* ===================================================== */}

      {activeTab === "applause" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("applause")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Applause is a structured way of reinforcing
              positive behavior, effort, and growth.
            </p>

            <p className={paragraphClass}>
              Recognition builds confidence, emotional
              stability, and motivation.
            </p>

            <p className={paragraphClass}>
              Self-applause encourages resilience and
              self-improvement.
            </p>

          </div>

          <button
            onClick={() =>
              navigate("/hafhapplause")
            }
            className={buttonClass}
          >
            Go for Applauses
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* RCA */}
      {/* ===================================================== */}

      {activeTab === "rca" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("rca")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Think5Whys RCA uncovers the real drivers
              behind problems instead of stopping at
              surface symptoms.
            </p>

            <p className={paragraphClass}>
              It improves clarity, deep reasoning,
              and structured decision-making.
            </p>

          </div>

          <button
            onClick={() =>
              navigate("/hafhrca")
            }
            className={buttonClass}
          >
            RCA / Causal Dashboard
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* BOOKS */}
      {/* ===================================================== */}

      {activeTab === "books" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("books")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Think5Whys BookWorld transforms reading into
              an intelligent immersive learning experience.
            </p>

            <p className={paragraphClass}>
              Users can highlight insights, take notes,
              and analyze learning behavior.
            </p>

          </div>

          <button
            onClick={() => navigate("/bookworld")}
            className={buttonClass}
          >
            Immersive BookWorld
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* MY BOOKS */}
      {/* ===================================================== */}

      {activeTab === "myBooks" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("myBooks")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Your personal immersive bookshelf stores all
              purchased and saved learning material.
            </p>

            <p className={paragraphClass}>
              Each book supports notes, audio,
              analytics, and learning insights.
            </p>

          </div>

          <button
            onClick={() => navigate("/bookshelf")}
            className={buttonClass}
          >
            Go to My Bookshelf
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* POCKET MONEY */}
      {/* ===================================================== */}

      {activeTab === "pocketMoney" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("pocketMoney")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Pocket earning activities encourage financial
              awareness and productivity.
            </p>

            <p className={paragraphClass}>
              Activities are designed to be safe,
              structured, and non-addictive.
            </p>

          </div>

          <button
            onClick={() => navigate("/bookworld")}
            className={buttonClass}
          >
            Explore Safe Earnings Space
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* ONBOARDING */}
      {/* ===================================================== */}

      {activeTab === "hafhprofile" && (

        <HAFHOnboarding
          t={t}
          hostelCode={hostelCode}
          role={userRole}
          currentTheme={currentTheme}
        />

      )}

    </div>
  );
};

export default HAFHParent;